import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import uniqid from 'uniqid';
import Case from 'case';
import { useAppTheme } from '@Components/Context';
import { buildDateTimeLabel } from '@Helpers/util';
import { DropButton, AppButton, ClaimStatusDetails } from '@Components/Control';
import { paths } from '@Components/configs';
import ListTableControls from '../ListTableControls';
import {
  TableWrapper,
  NoDataPlaceholder,
  tablePadding,
  renderTableHeader,
} from '../../SharedComponents';
import {
  StyledDropMenu,
} from '../../Dashboard/StyledDashboard';
import {
  ASSET_LIST_HEADER_MAP,
  ASSET_TYPE_OPTIONS,
  ASSET_SORT_OPTIONS,
  ASSET_DATE_FILTER_OPTIONS,
} from '../constants';

const AssetsTable = ({
  small,
  loading,
  assetsData,
  companyAssetConfig,
  dashboardPath,
  handlePage,
  currentPage,
  onQueryChange,
  onClickExport,
  query,
  paymentPending = false,
  isRedflagUpload = false,
  contentIdAssetUploadEnabled = false,
}) => {
  const { data = [], pagination = {} } = assetsData || {};
  const { totalResults = 0, limit = 25 } = pagination;
  const { theme: { page, button } } = useAppTheme();
  const assetLabels = (companyAssetConfig || []).map(({ assetLabel }) => assetLabel);

  const renderDataPoint = (uid, value, row) => {
    if (uid === 'totalClaims') {
      return (
        <ClaimStatusDetails
          key={uniqid()}
          small={small}
          claimStats={row.claimStats}
          totalClaims={row.totalClaims}
          textColor={page.tableText}
          buttonHighlight={button.primaryBg}
        />
      );
    }

    if (uid === 'timeCreated') {
      return (
        <Text size="1rem" color={page.tableText}>
          {buildDateTimeLabel(value)}
        </Text>
      );
    }
    const displayValue = uid === 'type' ? Case.title(value) : value;
    return (
      <Text size="1rem" color={page.tableText}>
        {displayValue}
      </Text>
    );
  };

  const renderActionButton = (row) => (
    <Box
      width={small ? '100%' : ASSET_LIST_HEADER_MAP.find(({ uid }) => uid === 'actions').size}
      align="start"
      key={uniqid()}
    >
      <DropButton
        withCloseDropContent={(onClose) => (
          <Box round="var(--app-radius)" pad="small" background={button.tableBg}>
            <StyledDropMenu
              plain
              label="Asset details"
              href={dashboardPath(row.id)}
              onClick={() => onClose()}
              textColor={page.tableText}
              hoverTextColor={button.hoverText}
              hoverIndicator={button.hoverBg}
            />
            <StyledDropMenu
              plain
              label="View claims"
              href={paths.productClaimsList.replace(':assetId', row.id)}
              onClick={() => onClose()}
              textColor={page.tableText}
              hoverTextColor={button.hoverText}
              hoverIndicator={button.hoverBg}
            />
          </Box>
        )}
      >
        {(Icon) => (
          <AppButton
            dropDown
            tableButtonStyle
            color={button.tableText}
            focusHighlight={page.focusHighlight}
            textSize="1rem"
            fontWeight={600}
            label="Actions"
            icon={<Icon color={button.tableIcon} size="0.9rem" />}
          />
        )}
      </DropButton>
    </Box>
  );

  const renderTableCell = (uid, row, display, size) => {
    if (uid === 'actions') {
      return renderActionButton(row);
    }
    return (
      <Box
        key={uniqid()}
        width={small ? '100%' : size}
        pad={{
          horizontal: small ? '0' : '0.5rem',
          vertical: small ? '0.25rem' : '0',
        }}
        direction="row"
        gap="0.5rem"
      >
        {small && (
          <Text size="0.875rem" color={page.tableHeader}>
            {display}
            :
          </Text>
        )}
        {renderDataPoint(uid, row[uid], row)}
      </Box>
    );
  };

  const renderTableContents = () => {
    if (loading || !data || data.length === 0) {
      let label = loading
        ? 'Loading assets...'
        : 'No assets uploaded yet, visit the assets upload page to get started.';

      if (isRedflagUpload) label = loading ? 'Loading uploads...' : 'No assets uploaded yet, track progress on the uploads page.';

      return (
        <NoDataPlaceholder
          noShadow
          noRound
          showRefreshButton={(
            !loading && (!data || data?.length === 0) && query && Object.keys(query).length > 0
          )}
          refreshQuery={() => onQueryChange('reset')}
          customButton={contentIdAssetUploadEnabled && (
            <AppButton
              overrideHover
              width="8.25rem"
              height="3rem"
              href={paths.productAssetUpload}
              level="dynamicLarge"
              color={button.primaryText}
              bgColor={button.primaryBg}
              fontWeight={600}
              label={isRedflagUpload ? 'View Uploads' : 'Upload Assets'}
            />
          )}
          buttonHighlight={page.highlight}
          loading={loading || !data}
          label={label}
          noDataLabel="No assets found matching the current filters."
          textColor={page.tableText}
          loaderColor={page.highlight}
        />
      );
    }

    return (
      <Box fill="horizontal" direction="column">
        {!small && (
          <Box
            fill="horizontal"
            wrap={false}
            direction="row"
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
            }}
          >
            {ASSET_LIST_HEADER_MAP.map(({ uid, display, size }) => (
              <Box
                key={uid}
                width={size}
                pad={{
                  horizontal: '0.5rem',
                  top: '1rem',
                  bottom: '0.5rem',
                }}
              >
                {renderTableHeader(display, small)}
              </Box>
            ))}
          </Box>
        )}
        {data.map((row) => (
          <Box
            key={row.id}
            direction={small ? 'column' : 'row'}
            align={small ? 'start' : 'center'}
            border={{
              color: page.tableBorder,
              size: '1px',
              style: 'solid',
              side: 'bottom',
            }}
            pad={{ vertical: '1rem', right: '0.5rem' }}
            background={page.tableBg}
            fill="horizontal"
          >
            {ASSET_LIST_HEADER_MAP.map(({ uid, display, size }) => (
              renderTableCell(uid, row, display, size)
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <TableWrapper
      zeroBasedPageIndex
      small={small}
      header="Assets"
      boxPad={tablePadding(small)}
      isPaged={totalResults > limit}
      totalResults={totalResults}
      currentResults={data?.length}
      selectedResultsCount={limit}
      overrideNextPageDisabled={pagination?.skip + pagination?.limit >= pagination?.totalResults}
      currentPage={currentPage}
      handlePage={handlePage}
      tableControls={(
        <ListTableControls
          small={small}
          query={query}
          onQueryChange={onQueryChange}
          onClickExport={onClickExport}
          assetLabels={assetLabels}
          searchPlaceholder="Search assets"
          typeOptions={ASSET_TYPE_OPTIONS}
          typeLabel="All Types"
          sortOptions={ASSET_SORT_OPTIONS}
          sortLabel="Sort By"
          dateFilterOptions={ASSET_DATE_FILTER_OPTIONS}
          paymentPending={paymentPending}
        />
      )}
    >
      <Box fill="horizontal" direction="column">
        {renderTableContents()}
      </Box>
    </TableWrapper>
  );
};

AssetsTable.propTypes = {
  small: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  assetsData: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      timeCreated: PropTypes.string.isRequired,
      metadata: PropTypes.objectOf(PropTypes.any),
      title: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      customId: PropTypes.string.isRequired,
      totalClaims: PropTypes.number.isRequired,
      claimStats: PropTypes.arrayOf(PropTypes.shape({
        count: PropTypes.number.isRequired,
        status: PropTypes.string.isRequired,
      })).isRequired,
    })).isRequired,
    pagination: PropTypes.shape({
      totalResults: PropTypes.number,
      skip: PropTypes.number,
      limit: PropTypes.number,
    }).isRequired,
  }),
  dashboardPath: PropTypes.func.isRequired,
  handlePage: PropTypes.func.isRequired,
  currentPage: PropTypes.number.isRequired,
  onQueryChange: PropTypes.func.isRequired,
  onClickExport: PropTypes.func.isRequired,
  query: PropTypes.object.isRequired,
  paymentPending: PropTypes.bool,
  isRedflagUpload: PropTypes.bool,
  companyAssetConfig: PropTypes.arrayOf(
    PropTypes.shape({
      channel: PropTypes.string.isRequired,
      assetLabel: PropTypes.string.isRequired,
      connectionType: PropTypes.string.isRequired,
    }),
  ),
  contentIdAssetUploadEnabled: PropTypes.bool,
};

export default AssetsTable;
